<script lang="ts">
  import DotGrid from '$components/reusable/DotGrid.svelte';
  import SectionTitle from '$components/reusable/SectionTitle.svelte';
  import Tag from '$components/reusable/Tag.svelte';
  import CornerSquare from '$components/reusable/wrappers/CornerSquare.svelte';
  import EnvelopeIcon from '$icons/EnvelopeIcon.svelte';
  import SparkleIcon from '$icons/SparkleIcon.svelte';
  import UserIcon from '$icons/UserIcon.svelte';
  import ArrowRight from '$icons/arrows/ArrowRight.svelte';
  import NoteIcon from '$icons/NoteIcon.svelte';
  import type { RetrieveProfileResponse } from '$shared/api';
  import Button from '$lib/components/reusable/Button.svelte';
  import EditPencilIcon from '$icons/edit/EditPencilIcon.svelte';

  export let profile: RetrieveProfileResponse;
  export let isOwnProfile = false;

  function shouldShowDetailCard(profile: RetrieveProfileResponse) {
    return (
      profile.extendedProfile.jobRoles.length > 0 ||
      profile.extendedProfile.interestedUseCases.length > 0 ||
      profile.extendedProfile.lookingForCollab
    );
  }

  function isProfileEmpty(profile: RetrieveProfileResponse) {
    return (
      profile.extendedProfile.jobRoles.length === 0 &&
      profile.extendedProfile.interestedUseCases.length === 0 &&
      profile.extendedProfile.lookingForCollab === false &&
      profile.extendedProfile.skills.length === 0 &&
      profile.extendedProfile.about.length === 0
    );
  }

  $: showDetailCard = shouldShowDetailCard(profile);
  $: showEmptyState = isProfileEmpty(profile);
</script>

{#if showDetailCard}
  <CornerSquare primary class="!mt-6 !h-fit">
    {#if profile.extendedProfile?.lookingForCollab}
      <div
        class="gap-x-md border-mint-dark-4 bg-mint-dark-1 !py-md px-lg flex !h-12 !min-h-[48px] w-full items-center overflow-hidden border-b-2"
      >
        <h2
          class="text-normal font-architekt text-mint-dark-12 whitespace-nowrap text-lg leading-6"
        >
          // LOOKING FOR TEAMMATES
        </h2>
        <DotGrid class="text-mint-dark-6" />
      </div>
    {/if}
    <div class="bg-mint-dark-1 px-2xl py-xl text-mint-dark-11">
      {#if profile.extendedProfile?.lookingForCollab}
        {#if profile.extendedProfile?.lookingToBuild}
          <p class="mb-lg pb-md break-words text-sm leading-5">
            {profile.extendedProfile?.lookingToBuild}
          </p>
        {/if}
      {/if}
      <div class="font-architekt text-mint-dark-11 space-y-5 text-sm leading-5">
        {#if profile.extendedProfile?.jobRoles && profile.extendedProfile?.jobRoles.length > 0}
          <div class="flex flex-col gap-2 md:flex-row md:flex-wrap">
            <div class="flex items-center gap-x-2">
              <div class="square" />
              <span>I am a</span>
            </div>
            {#each profile.extendedProfile?.jobRoles as role}
              <Tag size="xs" primary>{role}</Tag>
            {/each}
          </div>
        {/if}
        {#if profile.extendedProfile?.lookingForCollab && profile.extendedProfile?.rolesLookingFor && profile.extendedProfile?.rolesLookingFor.length > 0}
          <div class="flex flex-col gap-2 md:flex-row md:flex-wrap">
            <div class="flex items-center gap-x-2">
              <div class="square" />
              <span>Looking for</span>
            </div>
            {#each profile.extendedProfile?.rolesLookingFor as role}
              <Tag size="xs" primary>{role}</Tag>
            {/each}
          </div>
        {/if}
        {#if profile.extendedProfile?.interestedUseCases && profile.extendedProfile?.interestedUseCases.length > 0}
          <div class="flex flex-col gap-2 md:flex-row md:flex-wrap">
            <div class="flex items-center gap-x-2">
              <div class="square" />
              <span>Interested in</span>
            </div>
            {#each profile.extendedProfile?.interestedUseCases as useCase}
              <Tag size="xs" primary>{useCase}</Tag>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </CornerSquare>
{/if}

{#if profile.extendedProfile?.skills && profile.extendedProfile?.skills.length > 0}
  <div class="my-8 md:my-12">
    <SectionTitle icon={SparkleIcon} title="SKILLS" />
    <div class="mt-lg flex flex-wrap gap-2">
      {#each profile.extendedProfile?.skills as skill}
        <Tag tertiary>{skill}</Tag>
      {/each}
    </div>
  </div>
{/if}

{#if profile.extendedProfile?.about}
  <div class="my-8 w-full md:my-12">
    <SectionTitle icon={UserIcon} title="ABOUT" />
    <div class="mt-lg relative flex flex-col items-start gap-2">
      <p class="text-gray-dark-12 w-full break-words text-sm leading-5">
        {profile.extendedProfile?.about}
      </p>
    </div>
  </div>
{/if}

{#if profile.acceleratorProfile}
  <CornerSquare secondary class="!mt-6 !h-fit">
    <div
      class="gap-x-md border-orange-dark-2 bg-orange-dark-1 !py-md px-lg flex !h-12 !min-h-[48px] w-full items-center overflow-hidden border-b-2"
    >
      <h2
        class="text-normal font-architekt text-orange-dark-11 whitespace-nowrap text-lg leading-6"
      >
        // Accelerator Appendix
      </h2>
      <DotGrid class="text-orange-dark-6" />
    </div>
    <div class="bg-orange-dark-1 px-2xl py-xl text-mint-dark-11 mb-8">
      <div class="mb-2 w-full">
        <div class="relative flex flex-col gap-12">
          <div>
            <SectionTitle icon={EnvelopeIcon} title="GET IN TOUCH" />
            <div class="mt-lg relative flex flex-wrap items-start gap-6 sm:gap-[5rem]">
              <div class="flex flex-col gap-2">
                <span class="font-architekt text-gray-dark-11 text-sm"> Email </span>
                <a
                  href="mailto:{profile.acceleratorProfile.email}"
                  target="_blank"
                  class="text-gray-dark-12 hover:text-mint-dark-9 flex items-center gap-x-2"
                  on:click={(e) => e.stopPropagation()}
                >
                  <span>{profile.acceleratorProfile.email}</span>
                  <ArrowRight class="-rotate-45" />
                </a>
              </div>
              <div class="flex flex-col gap-2">
                <span class="font-architekt text-gray-dark-11 text-sm"> Telegram </span>
                <a
                  href="https://t.me/{profile.acceleratorProfile.telegramHandle}"
                  target="_blank"
                  class="text-gray-dark-12 hover:text-mint-dark-9 flex items-center gap-x-2"
                  on:click={(e) => e.stopPropagation()}
                >
                  <span>@{profile.acceleratorProfile.telegramHandle}</span>
                  <ArrowRight class="-rotate-45" />
                </a>
              </div>
            </div>
            <div>
              <p class="text-gray-dark-11 mt-6 w-full break-words text-sm leading-5">
                {profile.acceleratorProfile.contactOther ?? ''}
              </p>
            </div>
          </div>
          {#if profile.acceleratorProfile.profileAppendix}
            <div>
              <SectionTitle icon={NoteIcon} title="Founder Note" />
              <div>
                <p class="text-orange-dark-11 mt-5 w-full break-words text-sm leading-5">
                  {profile.acceleratorProfile.profileAppendix ?? ''}
                </p>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </CornerSquare>
{/if}

{#if showEmptyState}
  <div class="text-gray-dark-12 flex h-96 flex-col items-center justify-center gap-3">
    <h2 class="text-sm">Nothing to see here!</h2>
    {#if isOwnProfile}
      <p class="text-center">Ready to finish your profile? Click below to edit your profile.</p>
      <Button primary href="/onboarding/profile"
        >Complete Profile <EditPencilIcon slot="right-icon" class="h-4 w-4" /></Button
      >
    {:else}
      <p class="text-center">
        This user hasn't finished their profile yet. Check back soon to learn more about them.
      </p>
    {/if}
  </div>
{/if}
